import { useState } from 'react'
import { Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { ConfirmDialog } from '@/features/editor/components/confirm-dialog'
import {
  kindLabel,
  safeTitle,
} from '@/features/editor/components/knowledge/utils'
import { cn } from '@/lib/utils'
import type {
  KnowledgeEntry,
  KnowledgeKind,
} from '@/features/editor/types/knowledge.types'

type EntryListProps = {
  kind: KnowledgeKind
  entries: KnowledgeEntry[]
  selectedId: string
  onSelect: (id: string) => void
  onCreate: () => void
  onDelete: (id: string) => void
  onToggleEnabled: (id: string) => void
}

export function EntryList({
  kind,
  entries,
  selectedId,
  onSelect,
  onCreate,
  onDelete,
  onToggleEnabled,
}: EntryListProps) {
  const [deleteId, setDeleteId] = useState<string | null>(null)

  const pendingDelete = deleteId
    ? (entries.find((x) => x.id === deleteId) ?? null)
    : null

  return (
    <div className='border-border bg-background flex h-full flex-col rounded-2xl border'>
      {/* Header */}
      <div className='border-border flex shrink-0 items-center justify-between gap-3 border-b px-4 py-3'>
        <div className='text-sm font-medium'>
          {kindLabel(kind)}（{entries.length}）
        </div>
        <Button type='button' size='sm' onClick={onCreate}>
          新增条目
        </Button>
      </div>

      {entries.length === 0 ? (
        <div className='text-muted-foreground flex flex-1 items-center justify-center p-6 text-sm'>
          还没有条目。点击右上角“新增条目”开始创建。
        </div>
      ) : (
        <ul className='min-h-0 flex-1 overflow-auto p-2'>
          {entries.map((e) => {
            const active = e.id === selectedId
            return (
              <li key={e.id} className='mb-2 last:mb-0'>
                <div
                  role='button'
                  tabIndex={0}
                  onClick={() => onSelect(e.id)}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter' || ev.key === ' ') {
                      ev.preventDefault()
                      onSelect(e.id)
                    }
                  }}
                  className={cn(
                    'group flex w-full items-center justify-between gap-3 rounded-xl border px-3 py-2 text-left transition outline-none select-none',
                    'hover:bg-muted/20',
                    'focus-visible:ring-ring/30 focus-visible:ring-2',
                    active ? 'border-border/90 bg-muted/20' : 'border-border'
                  )}
                >
                  {/* 左：标题 + 状态 */}
                  <div className='min-w-0'>
                    <div className='flex min-w-0 items-center gap-2'>
                    <div
                      className={cn(
                        'truncate text-sm font-medium',
                        e.enabled ? '' : 'opacity-60'
                      )}
                    >
                      {safeTitle(e)}
                    </div>
                    </div>

                  </div>

                  {/* 右：管理操作（不触发选中） */}
                  <div
                    className='flex shrink-0 items-center gap-2'
                    onClick={(ev) => ev.stopPropagation()}
                  >
                    {/* 启用 / 禁用 */}
                    <Switch
                      checked={e.enabled}
                      onCheckedChange={() => onToggleEnabled(e.id)}
                    />

                    {/* 删除（弱化，hover/active 才明显） */}
                    <button
                      type='button'
                      onClick={(ev) => {
                        ev.stopPropagation()
                        setDeleteId(e.id)
                      }}
                      className={cn(
                        'text-muted-foreground hover:bg-muted/30 inline-flex items-center justify-center rounded-md p-2 transition',
                        active
                          ? 'opacity-100'
                          : 'opacity-0 group-hover:opacity-100'
                      )}
                      aria-label='删除条目'
                      title='删除'
                    >
                      <Trash2 className='h-4 w-4' />
                    </button>
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      )}

      {/* ✅ 删除确认弹窗（统一 UI，替代 window.confirm） */}
      <ConfirmDialog
        open={Boolean(deleteId)}
        title='删除条目'
        description={
          pendingDelete ? (
            <>
              确定删除「
              <span className='font-medium'>{safeTitle(pendingDelete)}</span>
              」吗？ 删除后无法恢复。
            </>
          ) : (
            '删除后无法恢复。是否继续？'
          )
        }
        destructive
        confirmText='删除'
        onCancel={() => setDeleteId(null)}
        onConfirm={() => {
          if (!deleteId) return
          onDelete(deleteId)
          setDeleteId(null)
        }}
      />
    </div>
  )
}
